import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {Search, X, AlertTriangle, Loader2} from 'lucide-react';
import clsx from 'clsx';
import {AdminService} from '@/modules/admin/services/adminService';
import type {OrganizationSearchResult} from '@/shared/types/api';
import {readOhdOrgId} from '@/shared/constants/ohd';
import {useOhdOrganizations} from '../hooks/useOhdOrganizations';
import type {OhdOrganizationListEntry} from '../types';

interface Props {
    open: boolean;
    onClose: () => void;
    /** Current MLD org id — the one we're configuring. */
    mldOrgId: number;
    /** Current MLD org name — shown in the reassignment confirm dialog. */
    mldOrgName: string;
    /** OHD org id currently linked to this MLD org (or undefined if not linked). */
    currentOhdOrgId?: number;
    /** Name of the currently linked OHD org, when known — shown in the change confirm dialog. */
    currentOhdOrgName?: string;
    /** All MLD orgs — used to build the "already linked to X" map. */
    allMldOrgs: OrganizationSearchResult[];
    /** Called after a successful mapping so the parent can invalidate queries. */
    onMapped: () => void;
}

/**
 * A selection that needs explicit confirmation before it saves. Either aspect
 * alone triggers the dialog; both can apply at once:
 * - `changingFrom`: this MLD org already has a different OHD link (old -> new).
 * - `linkedElsewhereMldOrg`: the target OHD org is linked to another MLD org,
 *   which will lose its link.
 */
interface PendingSelection {
    ohdOrg: OhdOrganizationListEntry;
    changingFrom: {id: number; name?: string} | null;
    linkedElsewhereMldOrg: OrganizationSearchResult | null;
}

const OhdFinderModal: React.FC<Props> = ({
    open,
    onClose,
    mldOrgId,
    mldOrgName,
    currentOhdOrgId,
    currentOhdOrgName,
    allMldOrgs,
    onMapped,
}) => {
    const [query, setQuery] = useState('');
    const [pending, setPending] = useState<PendingSelection | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const ohdOrgsQuery = useOhdOrganizations(open);
    const ohdOrgs = useMemo(() => ohdOrgsQuery.data ?? [], [ohdOrgsQuery.data]);

    // Build OHD id → MLD org map for "already linked" badges.
    const ohdToMld = useMemo(() => {
        const map = new Map<number, OrganizationSearchResult>();
        for (const org of allMldOrgs) {
            const id = readOhdOrgId(org.features as {ohd_organization_id?: number | string | null} | undefined);
            if (typeof id === 'number') map.set(id, org);
        }
        return map;
    }, [allMldOrgs]);

    // Name of the currently linked OHD org — prop first (stored on features),
    // falling back to the live OHD list for links saved before names were stored.
    const resolvedCurrentOhdOrgName =
        currentOhdOrgName ??
        (typeof currentOhdOrgId === 'number'
            ? ohdOrgs.find((o) => o.organization_id === currentOhdOrgId)?.organization_name
            : undefined);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return ohdOrgs;
        return ohdOrgs.filter((o) => {
            if (o.organization_name.toLowerCase().includes(q)) return true;
            if (String(o.organization_id).includes(q)) return true;
            return o.campaigns.some((c) => c.name.toLowerCase().includes(q));
        });
    }, [ohdOrgs, query]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setPending(null);
            setError(null);
            setSaving(false);
        }
    }, [open]);

    async function setMapping(ohd: OhdOrganizationListEntry) {
        // Linking an OHD org is what enables OHD Dashboard mode (Approach B):
        // the enable flag is a side effect of the link, never set on its own.
        await AdminService.updateOrganizationFeatures(mldOrgId, {
            ohd_organization_id: ohd.organization_id,
            ohd_organization_name: ohd.organization_name,
            enable_employee_health_dashboard: true,
        });
    }

    async function clearMapping(targetMldOrgId: number) {
        // Unlinking returns the org to MDX mode (Approach B). The API enforces
        // this too, but we clear the flag explicitly for clarity.
        await AdminService.updateOrganizationFeatures(targetMldOrgId, {
            ohd_organization_id: null,
            ohd_organization_name: null,
            enable_employee_health_dashboard: false,
        });
    }

    async function handleSelect(ohd: OhdOrganizationListEntry) {
        if (ohd.organization_id === currentOhdOrgId) {
            onClose();
            return;
        }
        const existingMld = ohdToMld.get(ohd.organization_id);
        const linkedElsewhereMldOrg = existingMld && existingMld.id !== mldOrgId ? existingMld : null;
        // Changing an existing link is how the wrong-org incident happened —
        // it always gets an explicit old -> new confirmation. Only a fresh
        // link to an unclaimed OHD org saves straight away.
        const changingFrom =
            typeof currentOhdOrgId === 'number' ? {id: currentOhdOrgId, name: resolvedCurrentOhdOrgName} : null;
        if (changingFrom || linkedElsewhereMldOrg) {
            setPending({ohdOrg: ohd, changingFrom, linkedElsewhereMldOrg});
            return;
        }
        try {
            setSaving(true);
            setError(null);
            await setMapping(ohd);
            onMapped();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save mapping');
        } finally {
            setSaving(false);
        }
    }

    async function confirmPending() {
        if (!pending) return;
        try {
            setSaving(true);
            setError(null);
            // Clear the previously-linked MLD org first, then attach to current.
            if (pending.linkedElsewhereMldOrg) {
                await clearMapping(pending.linkedElsewhereMldOrg.id);
            }
            await setMapping(pending.ohdOrg);
            onMapped();
            setPending(null);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save mapping');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={saving ? () => {} : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 py-16">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-2">
                            <Dialog.Panel className="w-full max-w-2xl rounded-xl border border-mld-panel-border bg-white shadow-xl">
                                <div className="flex items-center gap-3 border-b border-mld-panel-border px-4 py-3">
                                    <Search className="h-4 w-4 text-mld-muted-text" />
                                    <input
                                        autoFocus
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search OHD organizations by name, id, or campaign..."
                                        className="flex-1 border-none bg-transparent text-sm text-mld-menu-text focus:outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded p-1 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="max-h-[60vh] overflow-y-auto">
                                    {ohdOrgsQuery.isLoading ? (
                                        <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-mld-muted-text">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading OHD organizations…
                                        </div>
                                    ) : ohdOrgsQuery.isError ? (
                                        <div className="px-4 py-10 text-sm text-red-600">
                                            Unable to load OHD organizations:{' '}
                                            {ohdOrgsQuery.error instanceof Error ? ohdOrgsQuery.error.message : 'unknown error'}
                                        </div>
                                    ) : filtered.length === 0 ? (
                                        <div className="px-4 py-10 text-sm text-mld-muted-text">
                                            {query ? 'No OHD organizations match that search.' : 'No OHD organizations returned.'}
                                        </div>
                                    ) : (
                                        <ul className="divide-y divide-mld-panel-border">
                                            {filtered.map((o) => {
                                                const linkedMld = ohdToMld.get(o.organization_id);
                                                const linkedToThis = linkedMld?.id === mldOrgId;
                                                const linkedElsewhere = !!linkedMld && linkedMld.id !== mldOrgId;
                                                const latest = o.campaigns[0];
                                                return (
                                                    <li key={o.organization_id}>
                                                        <button
                                                            type="button"
                                                            disabled={saving || linkedToThis}
                                                            onClick={() => handleSelect(o)}
                                                            className={clsx(
                                                                'flex w-full items-center gap-3 px-4 py-3 text-left transition',
                                                                linkedToThis
                                                                    ? 'cursor-default bg-emerald-50/50'
                                                                    : 'hover:bg-[var(--mld-background)]',
                                                                saving && 'opacity-60'
                                                            )}>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="truncate text-sm font-semibold text-mld-menu-text">
                                                                        {o.organization_name}
                                                                    </span>
                                                                    <span className="text-[11px] text-mld-muted-text">
                                                                        #{o.organization_id}
                                                                    </span>
                                                                </div>
                                                                <div className="mt-0.5 truncate text-[12px] text-mld-muted-text">
                                                                    {latest ? `Latest campaign: ${latest.name}` : 'No campaigns'}
                                                                </div>
                                                            </div>
                                                            {linkedToThis && (
                                                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                                    Currently linked
                                                                </span>
                                                            )}
                                                            {linkedElsewhere && (
                                                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                                                    Linked to {linkedMld!.name}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>

                                {error && (
                                    <div className="border-t border-red-200 bg-red-50 px-4 py-2 text-[12px] text-red-700">
                                        {error}
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>

            {/* Change / reassignment confirm */}
            <Transition.Root show={!!pending} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={saving ? () => {} : () => setPending(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/50" />
                    </Transition.Child>
                    <div className="fixed inset-0 flex items-center justify-center px-4">
                        <Dialog.Panel className="w-full max-w-md rounded-xl border border-mld-panel-border bg-white shadow-xl">
                            <div className="flex items-start gap-3 border-b border-mld-panel-border px-5 py-4">
                                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                                <div>
                                    <div className="text-sm font-semibold text-mld-menu-text">
                                        {pending?.changingFrom ? 'Change the linked OHD organization?' : 'Reassign OHD mapping?'}
                                    </div>
                                    {pending?.changingFrom && (
                                        <p className="mt-1 text-[13px] leading-relaxed text-mld-text-secondary">
                                            <span className="font-medium">{mldOrgName}</span> is currently linked to{' '}
                                            <span className="font-medium">
                                                {pending.changingFrom.name ?? 'OHD organization'}
                                            </span>{' '}
                                            (#{pending.changingFrom.id}). Link it to{' '}
                                            <span className="font-medium">{pending.ohdOrg.organization_name}</span> (#
                                            {pending.ohdOrg.organization_id}) instead?
                                        </p>
                                    )}
                                    {!pending?.changingFrom && (
                                        <p className="mt-1 text-[13px] leading-relaxed text-mld-text-secondary">
                                            <span className="font-medium">{pending?.ohdOrg.organization_name}</span> (#
                                            {pending?.ohdOrg.organization_id}) is currently linked to{' '}
                                            <span className="font-medium">{pending?.linkedElsewhereMldOrg?.name}</span>.
                                            Reassign it to <span className="font-medium">{mldOrgName}</span>?
                                        </p>
                                    )}
                                    {pending?.linkedElsewhereMldOrg && (
                                        <p className="mt-2 text-[12px] text-mld-muted-text">
                                            {pending.changingFrom ? (
                                                <>
                                                    <span className="font-medium">{pending.ohdOrg.organization_name}</span> (#
                                                    {pending.ohdOrg.organization_id}) is currently linked to{' '}
                                                    <span className="font-medium">{pending.linkedElsewhereMldOrg.name}</span>,
                                                    which will lose its OHD link.
                                                </>
                                            ) : (
                                                <>The previous MLD org will lose its OHD link.</>
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {error && (
                                <div className="border-t border-red-200 bg-red-50 px-5 py-2 text-[12px] text-red-700">
                                    {error}
                                </div>
                            )}
                            <div className="flex justify-end gap-2 px-5 py-3">
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => setPending(null)}
                                    className="rounded-md px-3 py-1.5 text-xs font-semibold text-mld-text-secondary hover:bg-mld-menu-background">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={confirmPending}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-mld-menu-text px-3 py-1.5 text-xs font-semibold text-white hover:bg-mld-menu-text/90 disabled:opacity-60">
                                    {saving && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {pending?.changingFrom ? 'Change link' : 'Reassign'}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            </Transition.Root>
        </Transition.Root>
    );
};

export default OhdFinderModal;
